if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // GreenEZ: when the variant is already in the cart (possibly split
        // into multiple lines by Shopify's Buy-X-Get-Y cart transform),
        // consolidate via /cart/update.js instead of /cart/add.js so the
        // paid portion stays on a single line. Only safe when no custom
        // line item properties / selling_plan — otherwise a new line is
        // genuinely required and we fall back to /cart/add.js.
        const variantIdRaw = formData.get('id');
        const variantIdInt = parseInt(variantIdRaw, 10);
        const qtyToAdd = parseInt(formData.get('quantity') || '1', 10) || 1;
        let hasCustomProps = false;
        if (formData.forEach) {
          formData.forEach((_v, key) => {
            if (key && key.indexOf('properties[') === 0) hasCustomProps = true;
          });
        }
        if (formData.get('selling_plan')) hasCustomProps = true;

        const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const doOriginalAdd = () => fetch(`${routes.cart_add_url}`, config).then((r) => r.json());

        const firstCall = (hasCustomProps || !variantIdInt)
          ? doOriginalAdd()
          : fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' } })
              .then((r) => r.json())
              .then((cart) => {
                const matching = (cart.items || []).filter((it) => it.variant_id === variantIdInt);
                if (matching.length === 0) return doOriginalAdd();

                const currentTotal = matching.reduce((s, it) => s + it.quantity, 0);
                const newTotal = currentTotal + qtyToAdd;

                const primary = matching.find((it) => {
                  const fp = it.final_line_price != null ? it.final_line_price : it.final_price;
                  if (fp === 0) return false;
                  const props = it.properties || {};
                  return !Object.keys(props).some((k) =>
                    /^_?(bogo|free.?gift|gift|discount|reward|auto.?add)/i.test(k)
                  );
                }) || matching[0];

                const updates = {};
                matching.forEach((it) => {
                  updates[it.key] = (it.key === primary.key) ? newTotal : 0;
                });

                return fetch(rootUrl + 'cart/update.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({
                    updates: updates,
                    sections: formData.get('sections'),
                    sections_url: formData.get('sections_url'),
                  }),
                }).then((r) => r.json());
              })
              .catch(() => doOriginalAdd());

        firstCall
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
